/**
 * Job Status Utilities
 *
 * Classification and validation functions for job status states.
 * Centralizes business logic for determining job state and available actions.
 */

import { Job, JobStatus } from '@/types';
import { getJobStatus } from '@/utils/job';

/**
 * Determines if a job is currently running
 *
 * Includes both synchronous and asynchronous running states
 *
 * @param status - Job status value
 * @returns True if job is in running state
 */
export const isJobRunning = (status: JobStatus): boolean => {
  return status === JobStatus.Running || status === JobStatus.AsyncRunning;
};

/**
 * Determines if a job is queued for execution
 *
 * Includes both standard queued and async returning states
 *
 * @param status - Job status value
 * @returns True if job is queued
 */
export const isJobQueued = (status: JobStatus): boolean => {
  return status === JobStatus.Queued || status === JobStatus.AsyncReturning;
};

/**
 * Determines if a job has completed successfully
 *
 * @param status - Job status value
 * @returns True if job passed
 */
export const isJobCompleted = (status: JobStatus): boolean => {
  return status === JobStatus.Pass;
};

/**
 * Determines if a job has failed
 *
 * @param status - Job status value
 * @returns True if job failed
 */
export const isJobFailed = (status: JobStatus): boolean => {
  return status === JobStatus.Fail;
};

/**
 * Determines if a job is in a terminal state (completed or failed)
 *
 * @param status - Job status value
 * @returns True if job is finished
 */
export const isJobTerminal = (status: JobStatus): boolean => {
  return isJobCompleted(status) || isJobFailed(status);
};

/**
 * Determines if a job is in an active state (running or queued)
 *
 * @param status - Job status value
 * @returns True if job is active
 */
export const isJobActive = (status: JobStatus): boolean => {
  return isJobRunning(status) || isJobQueued(status);
};

/**
 * Determines if a job can be rerun
 *
 * Jobs can be rerun if they are not currently running or queued
 *
 * @param job - Job object
 * @returns True if job can be rerun
 */
export const canRerunJob = (job: Job): boolean => {
  const status = getJobStatus(job);
  return !isJobActive(status);
};

/**
 * Determines if a job can be canceled
 *
 * Jobs can be canceled if they are running or queued
 *
 * @param job - Job object
 * @returns True if job can be canceled
 */
export const canCancelJob = (job: Job): boolean => {
  const status = getJobStatus(job);
  return isJobActive(status);
};

/**
 * Determines if a job has downloadable artifacts
 *
 * Checks for completion status, filename, and download URL
 *
 * @param job - Job object
 * @returns True if job has downloadable results
 */
export const hasJobArtifacts = (job: Job): boolean => {
  const status = getJobStatus(job);
  return (
    isJobTerminal(status) &&
    Boolean(job.large_artifact_filename) &&
    Boolean(job.s3DownloadURL)
  );
};
